import React from 'react';
import { MarketingShell } from '@/seo/kit';

const TermsOfService = () => {
  return (
    <MarketingShell>
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <h1 className="text-4xl md:text-5xl" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>DRAWREADY — TERMS OF SERVICE</h1>
          <p className="mt-3" style={{ color: '#5B6678' }}>Effective Date: January 1, 2026</p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-8">

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">1. Overview</h2>
            <p className="text-slate-600 leading-relaxed">
              DrawReady ("DrawReady," "we," "our," or "us") provides a cloud-based software platform that helps real estate developers and construction professionals organize project costs, manage budgets, and generate standardized construction draw documentation (the "Platform").
            </p>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using DrawReady, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Platform.
            </p>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">2. Eligibility</h2>
            <p className="text-slate-600 leading-relaxed">You must:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Be at least 18 years old</li>
              <li>Use DrawReady solely for business or professional purposes</li>
              <li>Have authority to bind the business or entity you represent</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              DrawReady is not intended for consumer or personal use.
            </p>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">3. Account Registration & Responsibilities</h2>
            <p className="text-slate-600 leading-relaxed">You agree to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Be responsible for all activity occurring under your account</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You are solely responsible for all data, documents, and information uploaded to the Platform.
            </p>
          </section>

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">4. Nature of the Platform (Critical Disclaimer)</h2>
            
            <div className="border border-slate-200 rounded-md p-5">
              <h3 className="font-semibold text-slate-900 mb-2">What DrawReady DOES</h3>
              <ul className="list-disc list-inside text-slate-700 space-y-1 ml-4">
                <li>Organizes project financial data</li>
                <li>Generates reports and standardized documentation</li>
                <li>Provides project tracking and workflow tools</li>
              </ul>
            </div>

            <div className="border border-slate-200 rounded-md p-5">
              <h3 className="font-semibold text-slate-900 mb-2">What DrawReady DOES NOT</h3>
              <p className="text-slate-700 mb-2">DrawReady does not:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1 ml-4">
                <li>Act as a lender, bank, escrow agent, or financial institution</li>
                <li>Approve, deny, guarantee, or influence funding decisions</li>
                <li>Hold, transfer, or manage funds</li>
                <li>Provide legal, tax, accounting, or financial advice</li>
              </ul>
            </div>

            <p className="text-slate-600 leading-relaxed font-medium">
              All lending, reimbursement, and approval decisions are made entirely by third parties.
            </p>
          </section>

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">5. No Professional Advice</h2>
            <p className="text-slate-600 leading-relaxed">
              Any information provided through DrawReady is for organizational and informational purposes only. You acknowledge that you are responsible for consulting qualified professionals regarding legal, tax, accounting, financial, or construction matters.
            </p>
          </section>

          {/* Section 6 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">6. Fees & Payments</h2>
            <p className="text-slate-600 leading-relaxed">
              All fees are charged solely for access to the software Platform.
            </p>
            <p className="text-slate-600 leading-relaxed">Payment:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Does not guarantee draw approval, funding, or reimbursement</li>
              <li>Is non-contingent on lender or third-party outcomes</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              Failure to pay fees may result in suspension or termination of access.
            </p>
          </section>

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">7. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              The Platform, including all software, code, workflows, interfaces, designs, text, graphics, and documentation, is owned exclusively by DrawReady and is protected by intellectual property laws.
            </p>
            <p className="text-slate-600 leading-relaxed">You may not:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Copy, reproduce, modify, or distribute the Platform</li>
              <li>Reverse engineer, decompile, or attempt to extract source code</li>
              <li>Replicate workflows, user interfaces, or system logic</li>
              <li>Use the Platform to build a competing product</li>
            </ul>
            <p className="text-slate-600 leading-relaxed font-medium">
              All rights not expressly granted are reserved by DrawReady.
            </p>
          </section>

          {/* Section 8 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">8. User Data & License</h2>
            <p className="text-slate-600 leading-relaxed">
              You retain ownership of your uploaded data. By using the Platform, you grant DrawReady a limited, non-exclusive license to host, process, and display your data solely to operate and improve the Platform.
            </p>
            <p className="text-slate-600 leading-relaxed">
              DrawReady may use aggregated and anonymized data for analytics, benchmarking, and product improvement.
            </p>
          </section>

          {/* Section 9 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">9. Service Availability & Beta Disclaimer</h2>
            <p className="text-slate-600 leading-relaxed">
              The Platform may be updated, modified, or temporarily unavailable due to maintenance or technical issues.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Features offered during beta or testing phases are provided "as is" and may change without notice.
            </p>
          </section>

          {/* Section 10 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">10. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              The Platform is provided <strong>"AS IS"</strong> and <strong>"AS AVAILABLE."</strong>
            </p>
            <p className="text-slate-600 leading-relaxed">
              DrawReady makes no warranties, express or implied, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Accuracy or completeness of reports</li>
              <li>Platform uptime or uninterrupted access</li>
              <li>Fitness for a particular purpose</li>
            </ul>
            <p className="text-slate-600 leading-relaxed font-medium">
              Use of the Platform is at your own risk.
            </p>
          </section>

          {/* Section 11 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">11. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              To the maximum extent permitted by law, DrawReady shall not be liable for:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Lender or third-party decisions</li>
              <li>Project delays or cost overruns</li>
              <li>Lost profits, lost data, or business interruption</li>
              <li>Errors caused by user-provided information</li>
            </ul>
            <p className="text-slate-600 leading-relaxed font-medium">
              Total liability, if any, shall not exceed the fees paid by you to DrawReady in the 12 months preceding the claim.
            </p>
          </section>

          {/* Section 12 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">12. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless DrawReady from any claims, damages, or losses arising from:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Your use of the Platform</li>
              <li>Your data or documentation</li>
              <li>Your violation of these Terms</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">13. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              DrawReady may suspend or terminate access for:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Violation of these Terms</li>
              <li>Non-payment</li>
              <li>Misuse of the Platform</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You may cancel your account at any time. Upon termination, access to the Platform will cease.
            </p>
          </section>

          {/* Section 14 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">14. Dispute Resolution</h2>
            <p className="text-slate-600 leading-relaxed">
              Any dispute arising under these Terms shall be resolved through binding arbitration, and you waive any right to participate in a class action.
            </p>
          </section>

          {/* Section 15 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">15. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms are governed by the laws of the State of Wyoming, without regard to conflict-of-law principles.
            </p>
          </section>

          {/* Section 16 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">16. Changes to These Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update these Terms from time to time. Continued use of the Platform after changes become effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          {/* Section 17 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">17. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              For questions regarding these Terms, contact:
            </p>
            <p className="text-blue-600 font-medium">
              <a href="mailto:support@drawready.com">support@drawready.com</a>
            </p>
          </section>

        </div>
    </MarketingShell>
  );
};

export default TermsOfService;
